import React, { useEffect, useMemo, useState } from 'react'
import DataTable from 'react-data-table-component'
import { ChevronDown } from 'react-feather'
import LoadingAnimation from './LoadingAnimation'
import Skeleton from './Skeleton'

export const customStyles = {
  headRow: {
    style: {
      color: '#667085',
      backgroundColor: '#F9FAFB',
    },
  },
  headCells: {
    style: {
      wordBreak: 'break-word',
    },
  },
  cells: {
    style: {},
  },
  rows: {
    style: {
      '&:hover': {
        backgroundColor: '#F9FAFB',
      },
    },
  },
}

const useIsMobile = (breakpoint = 768) => {
  const [isMobile, setIsMobile] = useState(false)

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < breakpoint)
    check()
    window.addEventListener('resize', check)
    return () => window.removeEventListener('resize', check)
  }, [breakpoint])

  return isMobile
}

export const TableComponent = ({
  columns,
  data,
  isLoading,
  progressPending, // Add support for progressPending prop
  customStyles: overrideStyles,
  className,
  selectedData = [],
  onToggleSelect,
  expandableRows,
  expandableRowsComponent: ExpandableComponent,
  mobileExpandableRows = false,
  mobileExpandLabel = 'detail',
  renderMobileCard,
  testId = 'table-component',
  ...props
}) => {
  const isMobile = useIsMobile()
  // Use either isLoading or progressPending for consistent behavior
  const isActuallyLoading = isLoading || progressPending || props.progressPending

  const [mobileSelectedRows, setMobileSelectedRows] = useState([])
  const [mobileExpandedRows, setMobileExpandedRows] = useState({})

  useEffect(() => {
    setMobileSelectedRows([])
  }, [props.clearSelectedRows])

  const handleMobileSelectRow = (row) => {
    if (props.selectableRowDisabled && props.selectableRowDisabled(row)) return
    const isSelected = mobileSelectedRows.some((r) => r.id === row.id)
    let newSelected
    if (isSelected) {
      newSelected = mobileSelectedRows.filter((r) => r.id !== row.id)
    } else {
      newSelected = [...mobileSelectedRows, row]
    }
    setMobileSelectedRows(newSelected)
    if (props.onSelectedRowsChange) {
      props.onSelectedRowsChange({ selectedRows: newSelected })
    }
  }

  const handleMobileSelectAll = (e) => {
    const isChecked = e.target.checked
    let newSelected = []
    if (isChecked && data) {
      newSelected = data.filter(
        (row) => !(props.selectableRowDisabled && props.selectableRowDisabled(row))
      )
    }
    setMobileSelectedRows(newSelected)
    if (props.onSelectedRowsChange) {
      props.onSelectedRowsChange({ selectedRows: newSelected })
    }
  }

  const [freezeScopeClass] = useState(
    () => `pmb-freeze-scope-${Math.random().toString(36).slice(2, 10)}`,
  )

  const desktopColumns = useMemo(() => {
    if (!Array.isArray(columns)) return columns

    const parsePx = (v) => {
      if (!v) return null
      if (typeof v === 'number') return v
      const s = String(v)
      const n = parseInt(s.replace('px', ''), 10)
      return Number.isFinite(n) ? n : null
    }

    const resolveWidthPx = (col) =>
      parsePx(col?.width) ??
      parsePx(col?.minWidth) ??
      parsePx(col?.maxWidth) ??
      150

    const frozenLeftIdx = []
    const frozenRightIdx = []

    columns.forEach((col, idx) => {
      if (!col?.isFreeze) return
      if (String(col?.freezePosition).toLowerCase() === 'right') {
        frozenRightIdx.push(idx)
      } else {
        frozenLeftIdx.push(idx)
      }
    })

    if (frozenLeftIdx.length === 0 && frozenRightIdx.length === 0)
      return columns

    const leftOffsets = new Map()
    const isSelectable = !!props.selectableRows
    const baseLeftOffset = isSelectable ? 48 : 0
    let leftOffset = baseLeftOffset
    frozenLeftIdx.forEach((idx) => {
      leftOffsets.set(idx, leftOffset)
      leftOffset += resolveWidthPx(columns[idx])
    })

    const rightOffsets = new Map()
    let rightOffset = 0
    frozenRightIdx
      .slice()
      .reverse()
      .forEach((idx) => {
        rightOffsets.set(idx, rightOffset)
        rightOffset += resolveWidthPx(columns[idx])
      })

    const lastLeftIdx = frozenLeftIdx.length
      ? frozenLeftIdx[frozenLeftIdx.length - 1]
      : null
    const lastRightIdx = frozenRightIdx.length ? frozenRightIdx[0] : null

    return columns.map((col, idx) => {
      const isLeft = leftOffsets.has(idx)
      const isRight = rightOffsets.has(idx)
      if (!isLeft && !isRight) return col

      const stickyStyle = {
        position: 'sticky',
        zIndex: 1,
        backgroundColor: 'inherit',
        ...(isLeft ? { left: leftOffsets.get(idx) } : {}),
        ...(isRight ? { right: rightOffsets.get(idx) } : {}),
      }

      const stickyHeaderStyle = {
        position: 'sticky',
        zIndex: 2,
        backgroundColor: '#F9FAFB',
        ...(isLeft ? { left: leftOffsets.get(idx) } : {}),
        ...(isRight ? { right: rightOffsets.get(idx) } : {}),
      }

      const withShadow =
        (lastLeftIdx !== null && idx === lastLeftIdx) ||
        (lastRightIdx !== null && idx === lastRightIdx)

      return {
        ...col,
        style: {
          ...(col?.style ?? {}),
          ...stickyStyle,
          ...(withShadow
            ? {
              boxShadow: isLeft
                ? '2px 0 4px rgba(16, 24, 40, 0.06)'
                : '-2px 0 4px rgba(16, 24, 40, 0.06)',
            }
            : {}),
        },
        headerStyle: {
          ...(col?.headerStyle ?? {}),
          ...stickyHeaderStyle,
          ...(withShadow
            ? {
              boxShadow: isLeft
                ? '2px 0 4px rgba(16, 24, 40, 0.06)'
                : '-2px 0 4px rgba(16, 24, 40, 0.06)',
            }
            : {}),
        },
      }
    })
  }, [columns, props.selectableRows])

  const freezeHeaderCss = useMemo(() => {
    if (!Array.isArray(columns)) return ''

    const parsePx = (v) => {
      if (!v) return null
      if (typeof v === 'number') return v
      const s = String(v)
      const n = parseInt(s.replace('px', ''), 10)
      return Number.isFinite(n) ? n : null
    }

    const resolveWidthPx = (col) =>
      parsePx(col?.width) ??
      parsePx(col?.minWidth) ??
      parsePx(col?.maxWidth) ??
      150

    const frozenLeftIdx = []
    const frozenRightIdx = []

    columns.forEach((col, idx) => {
      if (!col?.isFreeze) return
      if (String(col?.freezePosition).toLowerCase() === 'right') {
        frozenRightIdx.push(idx)
      } else {
        frozenLeftIdx.push(idx)
      }
    })

    if (frozenLeftIdx.length === 0 && frozenRightIdx.length === 0) return ''

    const isSelectable = !!props.selectableRows
    const baseLeftOffset = isSelectable ? 48 : 0

    const leftOffsets = new Map()
    let leftOffset = baseLeftOffset
    frozenLeftIdx.forEach((idx) => {
      leftOffsets.set(idx, leftOffset)
      leftOffset += resolveWidthPx(columns[idx])
    })

    const rightOffsets = new Map()
    let rightOffset = 0
    frozenRightIdx
      .slice()
      .reverse()
      .forEach((idx) => {
        rightOffsets.set(idx, rightOffset)
        rightOffset += resolveWidthPx(columns[idx])
      })

    const lastLeftIdx = frozenLeftIdx.length
      ? frozenLeftIdx[frozenLeftIdx.length - 1]
      : null
    const lastRightIdx = frozenRightIdx.length ? frozenRightIdx[0] : null

    const scope = `.${freezeScopeClass}`

    let css = ''
    const baseNth = isSelectable ? 2 : 1

    const leftSelectors = (nth) =>
      [
        `${scope} .rdt_TableHeadRow .rdt_TableCol:nth-child(${nth})`,
        `${scope} .rdt_TableHeadRow > div:nth-child(${nth})`,
      ].join(',')

    const rightSelectors = (nth) =>
      [
        `${scope} .rdt_TableHeadRow .rdt_TableCol:nth-child(${nth})`,
        `${scope} .rdt_TableHeadRow > div:nth-child(${nth})`,
      ].join(',')

    if (isSelectable) {
      css += `${scope} .rdt_TableHeadRow .rdt_TableCol:nth-child(1){position:sticky;left:0;z-index:3;background-color:#F9FAFB}`
      css += `${scope} .rdt_TableRow .rdt_TableCell:nth-child(1){position:sticky;left:0;z-index:1;background-color:inherit}`
    }

    leftOffsets.forEach((offset, idx) => {
      const nth = idx + baseNth
      const shadow = idx === lastLeftIdx
      css += `${leftSelectors(nth)}{position:sticky;left:${offset}px;z-index:3;background:#F9FAFB;${shadow ? 'box-shadow:2px 0 4px rgba(16,24,40,0.06);' : ''}}`
    })
    rightOffsets.forEach((offset, idx) => {
      const nth = idx + baseNth
      const shadow = idx === lastRightIdx
      css += `${rightSelectors(nth)}{position:sticky;right:${offset}px;z-index:3;background:#F9FAFB;${shadow ? 'box-shadow:-2px 0 4px rgba(16,24,40,0.06);' : ''}}`
    })

    return css
  }, [columns, freezeScopeClass, props.selectableRows])

  if (isMobile) {
    return (
      <div className="space-y-3 pb-5">
        {props?.subHeaderComponent ?? null}

        {props.selectableRows && (
          <div className="d-flex align-items-center mb-2 px-2 bg-white border rounded p-3 shadow-sm">
            <input
              type="checkbox"
              className="form-check-input mt-0 me-2"
              style={{ cursor: 'pointer', width: '20px', height: '20px' }}
              data-testid={`${testId}-select-all-mobile`}
              checked={
                data && data.length > 0 && mobileSelectedRows.length > 0 &&
                data.filter((row) => !(props.selectableRowDisabled && props.selectableRowDisabled(row))).length === mobileSelectedRows.length
              }
              onChange={handleMobileSelectAll}
            />
            <span className="fw-medium text-dark">Pilih Semua</span>
          </div>
        )}

        {data && data.length > 0 ? (
          data.map((item, index) => {
            if (typeof renderMobileCard === 'function') {
              const isSelected = mobileSelectedRows.some((r) => r.id === item.id)
              const isDisabled = props.selectableRowDisabled ? props.selectableRowDisabled(item) : false
              return renderMobileCard(item, index, {
                isSelected,
                isDisabled,
                onSelect: () => handleMobileSelectRow(item)
              })
            }

            const tanggalTransaksi = columns.find(
              (col) => col.name?.props?.children === 'Tanggal transaksi',
            )
            const nomorTransaksi = columns.find(
              (col) => col.name?.props?.children === 'Nomor transaksi',
            )

            const otherColumns = columns.filter(
              (col) =>
                !['Tanggal transaksi', 'Nomor transaksi'].includes(
                  col.name?.props?.children,
                ),
            )

            return (
              <div
                key={index}
                className={`border p-3 shadow-sm mt-2 mb-3 bg-white`}
                data-testid={`${testId}-row-mobile-${item?.id ?? index}`}
                style={{ borderRadius: '12px' }}
              >
                <div className="row">
                  {(tanggalTransaksi || nomorTransaksi || props.selectableRows) && (
                    <div className="col-12 mb-2 border-bottom pb-2">
                      <div className="d-flex justify-content-between align-items-start">
                        <div className="d-flex align-items-start">
                          {props.selectableRows && (
                            <input
                              type="checkbox"
                              className="form-check-input mt-0 me-2"
                              style={{ cursor: 'pointer', width: '18px', height: '18px' }}
                              data-testid={`${testId}-row-checkbox-mobile-${item?.id ?? index}`}
                              checked={mobileSelectedRows.some((r) => r.id === item.id)}
                              disabled={props.selectableRowDisabled ? props.selectableRowDisabled(item) : false}
                              onChange={() => handleMobileSelectRow(item)}
                            />
                          )}
                          <div className="text-sm fw-medium pt-1">
                            {tanggalTransaksi &&
                              (typeof tanggalTransaksi.cell === 'function'
                                ? tanggalTransaksi.cell(item)
                                : item[tanggalTransaksi.selector])}
                          </div>
                        </div>
                        <div className="text-sm fw-medium text-end pt-1">
                          {nomorTransaksi &&
                            (typeof nomorTransaksi.cell === 'function'
                              ? nomorTransaksi.cell(item)
                              : item[nomorTransaksi.selector])}
                        </div>
                      </div>
                    </div>
                  )}

                  {otherColumns.map((col, colIdx) => {
                    const value =
                      typeof col.cell === 'function'
                        ? col.cell(item)
                        : typeof col.selector === 'function'
                          ? col.selector(item)
                          : item[col.selector]

                    const isEmptyLabel = !col.name || col.name === ''
                    const isCheckboxColumn =
                      React.isValidElement(col.name) && col.name.type === 'div'

                    return (
                      <div
                        key={colIdx}
                        className={`col-12 ${isEmptyLabel || isCheckboxColumn ? '' : 'col-md-6'
                          } py-1`}
                      >
                        {isCheckboxColumn ? (
                          <div className="d-flex justify-content-start">
                            {value}
                          </div>
                        ) : isEmptyLabel ? (
                          <div className="d-flex justify-content-start gap-2 flex-wrap">
                            {value}
                          </div>
                        ) : (
                          <div className="d-flex justify-content-between align-items-center">
                            <div className="text-muted small">{col.name}</div>
                            <div
                              className="text-end small text-truncate"
                              style={{
                                maxWidth: '60%',
                                overflow: 'hidden',
                                whiteSpace: 'nowrap',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {value}
                            </div>
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
                {mobileExpandableRows && expandableRows && ExpandableComponent &&
                  !props.expandableRowDisabled?.(item) && (
                    <button
                      type="button"
                      className="btn btn-link text-primary p-0 mt-2"
                      aria-expanded={!!mobileExpandedRows[item?.id ?? index]}
                      data-testid={`${testId}-expand-mobile-${item?.id ?? index}`}
                      onClick={() => setMobileExpandedRows((previous) => ({
                        ...previous,
                        [item?.id ?? index]: !previous[item?.id ?? index],
                      }))}
                    >
                      <ChevronDown
                        size={16}
                        style={{ transform: mobileExpandedRows[item?.id ?? index] ? 'rotate(180deg)' : undefined }}
                      />
                      <span className="ms-1">{mobileExpandedRows[item?.id ?? index] ? `Tutup ${mobileExpandLabel}` : `Lihat ${mobileExpandLabel}`}</span>
                    </button>
                  )}
                {expandableRows && ExpandableComponent &&
                (!mobileExpandableRows || mobileExpandedRows[item?.id ?? index]) ? (
                  <ExpandableComponent data={item} />
                ) : null}
              </div>
            )
          })
        ) : isActuallyLoading ? (
          <div className="space-y-3 py-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="border rounded p-3 bg-white">
                <div className="d-flex justify-content-between mb-3 pb-2 border-bottom">
                  <Skeleton width="100px" height="16px" />
                  <Skeleton width="80px" height="16px" />
                </div>
                <div className="space-y-2">
                  <div className="d-flex justify-content-between">
                    <Skeleton width="60px" height="14px" />
                    <Skeleton width="120px" height="14px" />
                  </div>
                  <div className="d-flex justify-content-between">
                    <Skeleton width="60px" height="14px" />
                    <Skeleton width="120px" height="14px" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-2 text-gray-500">Belum ada data.</div>
        )}
        {props?.paginationComponent ? props?.paginationComponent() : null}
      </div>
    )
  }

  return (
    <div className={freezeScopeClass} data-testid={testId}>
      {freezeHeaderCss ? <style>{freezeHeaderCss}</style> : null}
      <DataTable
        persistTableHead
        responsive
        customStyles={overrideStyles ?? customStyles}
        progressPending={isActuallyLoading}
        progressComponent={
          <div className="w-100">
            {[1, 2, 3, 4, 5].map((i) => (
              <div
                key={i}
                className="d-flex align-items-center p-3 border-bottom"
                style={{ height: '60px' }}
              >
                <div style={{ flex: 1 }} className="d-flex gap-3">
                  <Skeleton width="100%" height="20px" borderRadius="4px" />
                </div>
              </div>
            ))}
          </div>
        }
        className={className ?? 'border p-0 border-1 rounded-top'}
        sortIcon={<ChevronDown />}
        sortServer
        noDataComponent={<span className="my-1">Belum ada data.</span>}
        columns={desktopColumns}
        data={data}
        expandableRows={expandableRows}
        expandableRowsComponent={ExpandableComponent}
        {...props}
      />
    </div>
  )
}
